<template>
  <div style="background-color: #f8fafc; min-height: 100vh;">
    <v-layout wrap justify-center>
      <v-flex xs12 sm12 md12 lg12 pb-0 mb-0>
        <AppHeader class="pb-0 mb-0" />
      </v-flex>

      <v-flex xs12 sm11 md8 lg6 class="pa-4 pa-md-8">
        <v-card elevation="2" rounded="xl" class="pa-6 pa-md-8 my-6">
          
          <!-- Header Title & App Name -->
          <div class="text-center mb-6">
            <v-chip color="error" class="mb-3 font-weight-bold" small label text-color="white">
              ACCOUNT DELETION REQUEST
            </v-chip>
            <h1 class="text-h4 font-weight-bold grey--text text--darken-3">
              SARPA &amp; HWC App
            </h1>
            <p class="grey--text text--darken-1 text-subtitle-2 mt-2">
              Kerala Forest Department | Official Account Deletion Portal
            </p>
          </div>

          <v-divider class="mb-6"></v-divider>

          <!-- Deletion Policy Information Disclosure (Google Play Requirement) -->
          <v-alert colored-border border="left" color="warning" class="mb-6 text-body-2" elevation="1">
            <div class="font-weight-bold text-subtitle-1 mb-1 warning--text text--darken-3">
              <v-icon color="warning" class="mr-2">mdi-shield-alert</v-icon>
              Important Account Deletion Disclosures
            </div>
            <p class="mb-2">
              In accordance with Google Play Store User Data policies, users of the SARPA and HWC mobile applications can request permanent deletion of their user accounts.
            </p>
            <div class="font-weight-bold mt-3 mb-1">What data will be deleted:</div>
            <ul class="pl-4 mb-2">
              <li>User profile details (Name, Registered Mobile Number, Email)</li>
              <li>Authentication credentials, active login tokens, and session history</li>
              <li>Personal settings and rescuer profile affiliation</li>
            </ul>

            <div class="font-weight-bold mt-3 mb-1">What data is retained and why:</div>
            <ul class="pl-4 mb-0">
              <li>
                <strong>Wildlife Rescue &amp; Conflict Incident Logs:</strong> Historical snake rescue reports, wildlife conflict sightings, and official response records are retained permanently by the <em>Kerala Forest Department</em> for legal audit, statutory reporting, and wildlife conservation analytics.
              </li>
            </ul>
          </v-alert>

          <!-- Notification Snackbar -->
          <v-snackbar v-model="showSnackbar" :color="snackbarColor" top right :timeout="5000">
            {{ snackbarMsg }}
          </v-snackbar>

          <!-- SUCCESS STATE -->
          <div v-if="deleted" class="text-center py-8">
            <v-icon color="success" size="80" class="mb-4">mdi-check-circle-outline</v-icon>
            <h2 class="text-h5 font-weight-bold green--text text--darken-2 mb-2">
              Account Deletion Completed
            </h2>
            <p class="grey--text text--darken-2 text-body-1 max-width-500 mx-auto">
              Your SARPA user account and profile credentials have been successfully deleted. Your active login sessions have been revoked.
            </p>
            <v-btn color="primary" class="mt-4 px-6 rounded-lg font-weight-bold" to="/login">
              Return to Main Page
            </v-btn>
          </div>

          <!-- CREDENTIAL VERIFICATION FORM -->
          <div v-else>
            <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3 mb-2">
              Verify Account Credentials
            </div>
            <p class="grey--text text--darken-1 text-body-2 mb-4">
              Enter your registered mobile phone number, email ID, and password to verify ownership and confirm account deletion.
            </p>

            <v-form ref="deleteForm" v-model="formValid" @submit.prevent="confirmDeletion">
              <v-text-field
                v-model="phone"
                label="Registered Mobile Number"
                placeholder="e.g. 9876543210"
                outlined
                dense
                prepend-inner-icon="mdi-phone"
                :rules="phoneRules"
                type="tel"
                maxlength="10"
                class="mb-3"
              ></v-text-field>

              <v-text-field
                v-model="email"
                label="Registered Email Address (Optional)"
                placeholder="e.g. user@example.com"
                outlined
                dense
                prepend-inner-icon="mdi-email"
                type="email"
                class="mb-3"
              ></v-text-field>

              <v-text-field
                v-model="password"
                label="Password"
                placeholder="Enter your account password"
                outlined
                dense
                prepend-inner-icon="mdi-lock"
                :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPassword ? 'text' : 'password'"
                @click:append="showPassword = !showPassword"
                :rules="passwordRules"
                class="mb-4"
              ></v-text-field>

              <v-btn
                color="error"
                block
                large
                rounded="lg"
                class="font-weight-bold mt-2"
                :loading="loading"
                :disabled="!formValid"
                type="submit"
              >
                <v-icon left>mdi-account-remove</v-icon>
                Confirm &amp; Permanently Delete Account
              </v-btn>
            </v-form>
          </div>

        </v-card>
      </v-flex>
    </v-layout>
  </div>
</template>

<script>
import AppHeader from "@/components/Common/appHeaderP";
import axios from "axios";

export default {
  name: "DeleteAccount",
  components: {
    AppHeader,
  },
  data() {
    return {
      phone: "",
      email: "",
      password: "",
      showPassword: false,
      formValid: false,
      loading: false,
      deleted: false,
      showSnackbar: false,
      snackbarMsg: "",
      snackbarColor: "error",
      phoneRules: [
        (v) => !!v || "Registered mobile number is required",
        (v) => (v && v.length === 10) || "Mobile number must be 10 digits",
        (v) => /^\d+$/.test(v) || "Only numbers allowed",
      ],
      passwordRules: [
        (v) => !!v || "Password is required to confirm account deletion",
      ],
    };
  },
  methods: {
    confirmDeletion() {
      if (!this.phone || this.phone.length !== 10) {
        this.triggerSnackbar("Please enter a valid 10-digit mobile number.", "error");
        return;
      }
      if (!this.password) {
        this.triggerSnackbar("Please enter your account password.", "error");
        return;
      }
      this.loading = true;
      axios({
        method: "POST",
        url: "/user/delete-account",
        data: {
          phone: this.phone.trim(),
          email: this.email.trim(),
          password: this.password,
        },
      })
        .then((response) => {
          this.loading = false;
          if (response.data && response.data.status) {
            this.deleted = true;
            this.triggerSnackbar("Account deleted successfully.", "success");
          } else {
            this.triggerSnackbar(response.data.msg || "Deletion failed. Please check your credentials.", "error");
          }
        })
        .catch((err) => {
          this.loading = false;
          const errMsg = err.response && err.response.data && err.response.data.msg
            ? err.response.data.msg
            : "Error confirming deletion. Please check your phone number and password.";
          this.triggerSnackbar(errMsg, "error");
        });
    },

    triggerSnackbar(msg, color) {
      this.snackbarMsg = msg;
      this.snackbarColor = color;
      this.showSnackbar = true;
    },
  },
};
</script>

<style scoped>
.max-width-500 {
  max-width: 500px;
}
</style>
